import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useCallback, useEffect, useMemo } from 'react';

import Button from '@/components/Button';
import GenericDrawer from '@/components/GenericDrawer';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import SearchInput from '@/components/SearchInput';
import { ProductResponse } from '@/providers/HttpProvider/Fulfill/Inventory/types';
import { getProductId } from '@/types/fulfill/inventory';

import EditProduct from '../dependencies/EditProduct/EditProduct';
import { inventoryColumns } from './inventory-columns';
import KitCompositionDrawer from './KitCompositionDrawer';
import useInventory from './useInventory';

export interface InventoryTableProps {
  handleProductsSelected: (products: ProductResponse['products']) => void;
  onInventoryUpdated?: (getProducts: () => Promise<void>) => void;
  onActiveSkuCountChange?: (count: number) => void;
}

const DETAIL_PANEL_CHROME_HEIGHT = 60;
const DETAIL_PANEL_ROW_HEIGHT = 33;
const DETAIL_PANEL_TOTALS_EXTRA_PADDING = 8;
const FALLBACK_WAREHOUSE_COUNT = 4;

const InventoryTable = ({
  handleProductsSelected,
  onInventoryUpdated,
  onActiveSkuCountChange,
}: InventoryTableProps) => {
  const theme = useTheme();
  const hook = useInventory({ handleProductsSelected, onActiveSkuCountChange });

  const rows = useMemo(() => {
    const result: Array<ProductResponse['products'][number] | Record<string, unknown>> = [];
    hook.products.data.forEach((product) => {
      const id = getProductId(product);
      result.push(product);
      const expandState = hook.expandedRows[id];
      if (expandState) {
        result.push({ id: `${id}__detail`, isDetailRow: true, product, expandState });
      }
    });
    return result;
  }, [hook.products.data, hook.expandedRows]);

  const getRowHeight = useCallback(
    (params: {
      model: { isDetailRow?: boolean; product?: ProductResponse['products'][number] };
    }) => {
      if (!params.model.isDetailRow) return undefined;
      const warehouseCount =
        params.model.product?.warehouse_products?.length ?? FALLBACK_WAREHOUSE_COUNT;
      const rowCount = warehouseCount + 2;
      return (
        DETAIL_PANEL_CHROME_HEIGHT +
        rowCount * DETAIL_PANEL_ROW_HEIGHT +
        DETAIL_PANEL_TOTALS_EXTRA_PADDING
      );
    },
    [],
  );

  const getRowId = useCallback(
    (row: { isDetailRow?: boolean; id?: string }) =>
      row.isDetailRow
        ? (row.id as string)
        : getProductId(row as ProductResponse['products'][number]),
    [],
  );

  const isRowSelectable = useCallback(
    (params: { row: { isDetailRow?: boolean } }) => !params.row.isDetailRow,
    [],
  );

  const getRowClassName = useCallback(
    (params: { row: { isDetailRow?: boolean } }) =>
      params.row.isDetailRow ? 'inventory-detail-row' : '',
    [],
  );

  useEffect(() => {
    if (onInventoryUpdated) {
      onInventoryUpdated(hook.getProducts);
    }
  }, [hook.getProducts, onInventoryUpdated]);

  return (
    <Paper elevation={3} sx={{ padding: `${theme.spacing(2)} 0 0` }}>
      <Box
        sx={{
          padding: `0 ${theme.spacing(4)}`,
          flexDirection: { xxs: 'column', xl: 'row' },
          gap: { xxs: '0.5rem', xl: 0 },
          alignItems: 'center',
        }}
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        height="auto"
      >
        <SearchInput
          placeholder="Buscar por SKU o Nombre"
          buttonText="Buscar"
          sx={{ width: '100%' }}
          handleKeyPress={hook.handleKeyPress}
          onChange={hook.onChgSearch}
          onClick={hook.onSearch}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xxs: 'column', xs: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {hook.selected.length > 0 && (
            <Button
              onClick={hook.clearSelection}
              variant="text"
              size="small"
              startIcon={<ChecklistRtlIcon color="primary" />}
            >
              Limpiar selección ({hook.selected.length})
            </Button>
          )}
          <Button
            onClick={hook.getReport}
            variant="text"
            size="small"
            startIcon={<CloudDownloadOutlinedIcon color="primary" />}
          >
            Descargar reporte
          </Button>
        </Box>
      </Box>
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Box sx={{ minWidth: '900px' }}>
          <AgaveDataGrid
            columns={inventoryColumns({
              handleEditProduct: hook.handleEditProduct,
              handleViewKitComposition: hook.handleViewKitComposition,
              expandedRows: hook.expandedRows,
              toggleWarehouseDetail: hook.toggleWarehouseDetail,
              finalizeWarehouseCollapse: hook.finalizeWarehouseCollapse,
            })}
            rows={rows}
            localeText={{
              noRowsLabel: hook.search
                ? 'No encontramos ningún producto que coincida con tu búsqueda.'
                : 'No hay productos disponibles',
            }}
            rowCount={hook.products.metadata.total_records}
            checkboxSelection={true}
            disableRowSelectionOnClick={true}
            disableCursorPointer
            autoHeight
            getRowId={getRowId}
            getRowHeight={getRowHeight}
            isRowSelectable={isRowSelectable}
            getRowClassName={getRowClassName}
            hideFooterSelectedRowCount={true}
            paginationModel={{
              page: hook.pagination.filters.page as number,
              pageSize: hook.pagination.filters.page_size as number,
            }}
            disableColumnMenu={true}
            paginationMode="server"
            onPaginationModelChange={hook.pagination.onPaginationModelChange}
            rowSelectionModel={{ ids: new Set(hook.selected), type: 'include' }}
            onRowSelectionModelChange={(selectionModel) =>
              hook.handleSelect(Array.from(selectionModel.ids) as string[])
            }
            sx={{
              '& .inventory-detail-row': {
                cursor: 'default !important',
              },
              '& .inventory-detail-row:hover': {
                backgroundColor: 'transparent !important',
              },
              '& .inventory-detail-row .MuiDataGrid-cellCheckbox': {
                display: 'none',
              },
              '& .inventory-detail-row [data-field="thumbnail"]': {
                left: '0px !important',
                width: '100% !important',
                padding: '0 !important',
              },
            }}
          />
        </Box>
      </Box>
      <GenericDrawer
        open={hook.editProduct.open}
        onClose={hook.closeEditProduct}
        title="Editar producto"
        iconTitle={<EditIcon color="primary" />}
      >
        <EditProduct
          product={hook.editProduct.product!}
          getProduct={hook.getProducts}
          onClose={hook.closeEditProduct}
        />
      </GenericDrawer>
      <KitCompositionDrawer
        product={hook.kitComposition.product}
        open={hook.kitComposition.open}
        onClose={hook.closeKitComposition}
      />
    </Paper>
  );
};

export default InventoryTable;
